import {
  MutableRefObject,
  RefObject,
  useEffect,
  useLayoutEffect,
  useRef
} from 'react';
import { OverlayScrollbarsComponentRef } from 'overlayscrollbars-react';
import { getTwoFingerSpan } from '@pages/MediaViewer/AssetViewer/PdfCard/utils';
import { IAssetViewerStateContextValue } from '@context/AssetViewerStateContext';

const minZoom = 0.1;
const maxZoom = 3;
const minZoomRatio = 0.7;

export type PdfPinchGestureState = {
  isActive: boolean;
  startSpan: number;
  baseZoom: number;
  anchorPage: number;
  anchorRatioX: number;
  anchorRatioY: number;
  anchorViewportOffsetX: number;
  anchorViewportOffsetY: number;
  anchorContentX: number;
  anchorContentY: number;
};

type UseMobilePdfPinchZoomParams = {
  pdfScrollViewport: Element | null;
  wrapperRef: RefObject<OverlayScrollbarsComponentRef<'div'>>;
  contentRef: RefObject<HTMLDivElement>;
  pageRefsMap: MutableRefObject<Map<number, HTMLDivElement>>;
  pdfPinchGestureRef: MutableRefObject<PdfPinchGestureState>;
  fitZoomRef: MutableRefObject<number>;
  skipActivePageScrollSyncRef: MutableRefObject<boolean>;
  zoomFactor: number;
  setZoomFactor: IAssetViewerStateContextValue['setZoomFactor'];
  updateActivePageFromScroll: () => void;
};

export function useMobilePdfPinchZoom({
  pdfScrollViewport,
  wrapperRef,
  contentRef,
  pageRefsMap,
  pdfPinchGestureRef,
  fitZoomRef,
  skipActivePageScrollSyncRef,
  zoomFactor,
  setZoomFactor,
  updateActivePageFromScroll
}: UseMobilePdfPinchZoomParams) {
  const zoomRef = useRef(zoomFactor);
  zoomRef.current = zoomFactor;

  const pendingPinchZoomRef = useRef<number | null>(null);
  const pendingZoomScrollRef = useRef<{
    page: number;
    ratioX: number;
    ratioY: number;
    viewportOffsetX: number;
    viewportOffsetY: number;
    scale: number;
  } | null>(null);

  useEffect(() => {
    if (!pdfScrollViewport) return undefined;

    const pinch = pdfPinchGestureRef.current;

    const capturePinchAnchor = (touches: TouchList) => {
      const content = contentRef.current;
      if (!content) return;
      const viewportRect = pdfScrollViewport.getBoundingClientRect();
      const contentRect = content.getBoundingClientRect();

      const rawFocalX =
        touches.length >= 2
          ? (touches[0].clientX + touches[1].clientX) / 2
          : viewportRect.left + viewportRect.width / 2;
      const rawFocalY =
        touches.length >= 2
          ? (touches[0].clientY + touches[1].clientY) / 2
          : viewportRect.top + viewportRect.height / 2;

      const focalX = Math.min(
        Math.max(rawFocalX, viewportRect.left),
        viewportRect.right
      );
      const focalY = Math.min(
        Math.max(rawFocalY, viewportRect.top),
        viewportRect.bottom
      );

      pinch.anchorViewportOffsetX = focalX - viewportRect.left;
      pinch.anchorViewportOffsetY = focalY - viewportRect.top;
      pinch.anchorContentX = focalX - contentRect.left;
      pinch.anchorContentY = focalY - contentRect.top;

      pinch.anchorPage = 0;
      pinch.anchorRatioX = 0;
      pinch.anchorRatioY = 0;
      pageRefsMap.current.forEach((pageEl, page) => {
        const rect = pageEl.getBoundingClientRect();
        if (focalY >= rect.top && focalY <= rect.bottom) {
          pinch.anchorPage = page;
          pinch.anchorRatioX = rect.width
            ? (focalX - rect.left) / rect.width
            : 0;
          pinch.anchorRatioY = rect.height
            ? (focalY - rect.top) / rect.height
            : 0;
        }
      });
    };

    const applyPinchPreview = (nextZoom: number) => {
      const content = contentRef.current;
      if (!content || pinch.baseZoom <= 0) return;
      const scale = nextZoom / pinch.baseZoom;
      content.style.transformOrigin = `${pinch.anchorContentX}px ${pinch.anchorContentY}px`;
      content.style.transform = `scale(${scale})`;
    };

    const clearPinchPreview = () => {
      const content = contentRef.current;
      if (!content) return;
      content.style.transform = '';
      content.style.transformOrigin = '';
    };

    const commitPinchZoom = () => {
      const nextZoom = pendingPinchZoomRef.current;
      pendingPinchZoomRef.current = null;
      clearPinchPreview();
      if (nextZoom == null || nextZoom === zoomRef.current) return;
      pendingZoomScrollRef.current = {
        page: pinch.anchorPage,
        ratioX: pinch.anchorRatioX,
        ratioY: pinch.anchorRatioY,
        viewportOffsetX: pinch.anchorViewportOffsetX,
        viewportOffsetY: pinch.anchorViewportOffsetY,
        scale: pinch.baseZoom > 0 ? nextZoom / pinch.baseZoom : 1
      };
      setZoomFactor(nextZoom);
    };

    const onTouchStart = (e: Event) => {
      const touchEvent = e as TouchEvent;
      if (touchEvent.touches.length < 2) return;
      const span = getTwoFingerSpan(touchEvent.touches);
      if (span < 10) return;
      if (!pinch.isActive) {
        pinch.isActive = true;
        pinch.startSpan = span;
        pinch.baseZoom = zoomRef.current;
        pendingPinchZoomRef.current = null;
        clearPinchPreview();
        capturePinchAnchor(touchEvent.touches);
      }
    };

    const onTouchMove = (e: Event) => {
      const touchEvent = e as TouchEvent;
      if (!pinch.isActive || touchEvent.touches.length < 2) return;
      const span = getTwoFingerSpan(touchEvent.touches);
      if (pinch.startSpan < 10) return;
      const lowerBound = Math.max(minZoom, fitZoomRef.current * minZoomRatio);
      const nextZoom = Math.min(
        maxZoom,
        Math.max(lowerBound, pinch.baseZoom * (span / pinch.startSpan))
      );
      touchEvent.preventDefault();
      pendingPinchZoomRef.current = nextZoom;
      applyPinchPreview(nextZoom);
    };

    const onTouchEnd = (e: Event) => {
      const touchEvent = e as TouchEvent;
      if (touchEvent.touches.length < 2 && pinch.isActive) {
        pinch.isActive = false;
        commitPinchZoom();
      }
    };

    const listenerCapturePassive = { capture: true, passive: true } as const;
    const listenerCaptureNotPassive = {
      capture: true,
      passive: false
    } as const;

    pdfScrollViewport.addEventListener(
      'touchstart',
      onTouchStart,
      listenerCapturePassive
    );
    pdfScrollViewport.addEventListener(
      'touchmove',
      onTouchMove,
      listenerCaptureNotPassive
    );
    pdfScrollViewport.addEventListener(
      'touchend',
      onTouchEnd,
      listenerCapturePassive
    );
    pdfScrollViewport.addEventListener(
      'touchcancel',
      onTouchEnd,
      listenerCapturePassive
    );

    return () => {
      pinch.isActive = false;
      pendingPinchZoomRef.current = null;
      clearPinchPreview();
      pdfScrollViewport.removeEventListener(
        'touchstart',
        onTouchStart,
        listenerCapturePassive
      );
      pdfScrollViewport.removeEventListener(
        'touchmove',
        onTouchMove,
        listenerCaptureNotPassive
      );
      pdfScrollViewport.removeEventListener(
        'touchend',
        onTouchEnd,
        listenerCapturePassive
      );
      pdfScrollViewport.removeEventListener(
        'touchcancel',
        onTouchEnd,
        listenerCapturePassive
      );
    };
  }, [
    pdfScrollViewport,
    setZoomFactor,
    contentRef,
    pageRefsMap,
    pdfPinchGestureRef,
    fitZoomRef
  ]);

  useLayoutEffect(() => {
    const pending = pendingZoomScrollRef.current;
    if (!pending) return;
    pendingZoomScrollRef.current = null;

    const viewport = wrapperRef.current?.osInstance()?.elements()?.viewport;
    if (!viewport) return;

    const maxScrollTop = viewport.scrollHeight - viewport.clientHeight;
    const maxScrollLeft = viewport.scrollWidth - viewport.clientWidth;

    const pageEl = pending.page
      ? pageRefsMap.current.get(pending.page)
      : undefined;

    let targetTop: number;
    let targetLeft: number;

    if (pageEl) {
      const viewportRect = viewport.getBoundingClientRect();
      const pageRect = pageEl.getBoundingClientRect();
      targetTop =
        viewport.scrollTop +
        (pageRect.top - viewportRect.top) +
        pending.ratioY * pageRect.height -
        pending.viewportOffsetY;
      targetLeft =
        viewport.scrollLeft +
        (pageRect.left - viewportRect.left) +
        pending.ratioX * pageRect.width -
        pending.viewportOffsetX;
    } else {
      targetTop =
        (viewport.scrollTop + pending.viewportOffsetY) * pending.scale -
        pending.viewportOffsetY;
      targetLeft =
        (viewport.scrollLeft + pending.viewportOffsetX) * pending.scale -
        pending.viewportOffsetX;
    }

    skipActivePageScrollSyncRef.current = true;
    viewport.scrollTo({
      top: Math.max(0, Math.min(targetTop, maxScrollTop)),
      left: Math.max(0, Math.min(targetLeft, maxScrollLeft)),
      behavior: 'instant'
    });
    window.setTimeout(() => {
      skipActivePageScrollSyncRef.current = false;
      updateActivePageFromScroll();
    }, 0);
  }, [
    zoomFactor,
    updateActivePageFromScroll,
    wrapperRef,
    pageRefsMap,
    skipActivePageScrollSyncRef
  ]);
}
